import { LINE_PRESSURE_PRECISION } from 'constants/samplePoint';
import {
  UnitElectricCurrent,
  UnitEnergyFlux,
  UnitEnergyUsage,
  UnitFlowMinuteSm,
  UnitPressureLg,
  UnitPressureMd,
  UnitRain,
  UnitTemperature,
  UnitVoltage,
  UnitWindSpeed
} from 'utils/get-unit-by-country';

/** Doc: https://farmbot-au.atlassian.net/wiki/spaces/DEV/pages/2267938817/Product+Spec+Weather+Stations#Weather-Sensor-Decimal-Placement-(precision) */
const ENERGY_UNIT_SYMBOL_TO_PRECISION: Record<
  UnitElectricCurrent | UnitEnergyFlux | UnitEnergyUsage | UnitVoltage,
  number
> = {
  'A': 1,
  'V': 2,
  'W/m²': 1,
  'kWh': 2
};

/**
 * This constant was relocated. Its original formation was in commit (08/2023) 6333dd3e9ce3525e4e2a6ab841c12988dbdfc245,
 * PR https://bitbucket.org/farmbotteam/farmbot-web-app/pull-requests/1050,
 * and we are not aware of a Confluence doc for it yet.
 */
const PIPE_FLOW_RATE_PRECISION = 1;
const FLOW_RATE_UNIT_SYMBOL_TO_PRECISION: Record<UnitFlowMinuteSm, number> = {
  'L/min': PIPE_FLOW_RATE_PRECISION,
  'gal/min': PIPE_FLOW_RATE_PRECISION
  // TODO: UnitFlowMinuteLg, UnitFlowHour
};

/** Doc: https://farmbot-au.atlassian.net/wiki/spaces/DEV/pages/2267938817/Product+Spec+Weather+Stations#Weather-Sensor-Decimal-Placement-(precision) */
const PRESSURE_UNIT_SYMBOL_TO_PRECISION: Record<UnitPressureMd | UnitPressureLg, number> = {
  'hPa': 0,
  'inHg': 2,
  'kPa': LINE_PRESSURE_PRECISION,
  'psi': LINE_PRESSURE_PRECISION
};

/** Doc: https://farmbot-au.atlassian.net/wiki/spaces/DEV/pages/2267938817/Product+Spec+Weather+Stations#Weather-Sensor-Decimal-Placement-(precision) */
const SPEED_UNIT_SYMBOL_TO_PRECISION: Record<UnitWindSpeed, number> = {
  'km/h': 1,
  'mph': 1
};

/** Doc: https://farmbot-au.atlassian.net/wiki/spaces/DEV/pages/2267938817/Product+Spec+Weather+Stations#Weather-Sensor-Decimal-Placement-(precision) */
const RAIN_UNIT_SYMBOL_TO_PRECISION: Record<UnitRain, number> = {
  'in': 2,
  'mm': 1
};

/** Doc: https://farmbot-au.atlassian.net/wiki/spaces/DEV/pages/2267938817/Product+Spec+Weather+Stations#Weather-Sensor-Decimal-Placement-(precision) */
const TEMPERATURE_UNIT_SYMBOL_TO_PRECISION: Record<UnitTemperature, number> = {
  '°C': 1,
  '°F': 1
};

// Ideally, we should support all unit symbols. But given the legacy code, it will take a while to refactor.
// TODO: https://farmbot.atlassian.net/browse/FMBT-7270
type SupportedUnitSymbol = Exclude<
  | UnitElectricCurrent
  | UnitEnergyFlux
  | UnitEnergyUsage
  | UnitFlowMinuteSm
  | UnitPressureLg
  | UnitPressureMd
  | UnitRain
  | UnitTemperature
  | UnitVoltage
  | UnitWindSpeed
  , '%'>;

export const UNIT_SYMBOL_TO_PRECISION: Record<SupportedUnitSymbol, number> = {
  ...ENERGY_UNIT_SYMBOL_TO_PRECISION,
  ...FLOW_RATE_UNIT_SYMBOL_TO_PRECISION,
  ...PRESSURE_UNIT_SYMBOL_TO_PRECISION,
  ...RAIN_UNIT_SYMBOL_TO_PRECISION,
  ...SPEED_UNIT_SYMBOL_TO_PRECISION,
  ...TEMPERATURE_UNIT_SYMBOL_TO_PRECISION
};

// ==============================
// A SPECIAL HANDLING FOR PERCENTAGE, FOR NOW. BECAUSE UNLIKE UNIT_SYMBOL_TO_PRECISION, IT HOLDS MORE THAN ONE PRECISION.
// ==============================
export enum PrecisionType {
  _DEFAULT = '_default',
  PERCENTAGE_DAM = 'percentage_dam',
  PERCENTAGE_DISTRIBUTION = 'percentage_distribution',
  PERCENTAGE_HUMIDITY = 'percentage_humidity'
} // Add more precision types here if needed; as of now, only percentage needs it.

type PercentagePrecisionType =
  | PrecisionType._DEFAULT
  | PrecisionType.PERCENTAGE_DAM
  | PrecisionType.PERCENTAGE_DISTRIBUTION
  | PrecisionType.PERCENTAGE_HUMIDITY;

/** Doc: https://farmbot-au.atlassian.net/wiki/spaces/DEV/pages/2267938817/Product+Spec+Weather+Stations#Weather-Sensor-Decimal-Placement-(precision) */
export const PERCENTAGE_PRECISIONS: Record<PercentagePrecisionType, number> = {
  [PrecisionType._DEFAULT]: 0,
  [PrecisionType.PERCENTAGE_DAM]: 2,
  [PrecisionType.PERCENTAGE_DISTRIBUTION]: 1,
  [PrecisionType.PERCENTAGE_HUMIDITY]: 1
};